import * as signalR from '@microsoft/signalr';
import type { AxiosInstance } from 'axios';

const toSignalRContent = (data: unknown): string | ArrayBuffer => {
  if (data instanceof ArrayBuffer) return data;
  if (typeof data === 'string') return data;
  return JSON.stringify(data);
};

export interface SignalRConnectionOptions {
  url: string;
  axiosInstance: AxiosInstance;
  accessTokenFactory: () => Promise<string>;
}

/**
 * Creates a SignalR hub connection backed by an Axios HTTP client.
 * Shared between customer and participant UIs.
 */
export const createSignalRConnection = ({
  url,
  axiosInstance,
  accessTokenFactory,
}: SignalRConnectionOptions): signalR.HubConnection => {
  return new signalR.HubConnectionBuilder()
    .withUrl(url, {
      httpClient: {
        get: async (requestUrl: string, options?: signalR.HttpRequest) => {
          const response = await axiosInstance.get(requestUrl, {
            headers: options?.headers,
          });
          return {
            statusCode: response.status,
            statusText: response.statusText,
            content: toSignalRContent(response.data),
            headers: response.headers,
          } as signalR.HttpResponse;
        },
        post: async (requestUrl: string, options?: signalR.HttpRequest) => {
          const response = await axiosInstance.post(requestUrl, options?.content, {
            headers: options?.headers,
          });
          return {
            statusCode: response.status,
            statusText: response.statusText,
            content: toSignalRContent(response.data),
            headers: response.headers,
          } as signalR.HttpResponse;
        },
        delete: async (requestUrl: string, _options?: signalR.HttpRequest) => {
          const response = await axiosInstance.delete(requestUrl);
          return {
            statusCode: response.status,
            statusText: response.statusText,
            content: toSignalRContent(response.data),
            headers: response.headers,
          } as signalR.HttpResponse;
        },
        send: async (request: signalR.HttpRequest) => {
          const response = await axiosInstance({
            method: request.method as string,
            url: request.url as string,
            data: request.content,
            headers: request.headers,
            responseType: request.responseType as any,
          });
          return {
            statusCode: response.status,
            statusText: response.statusText,
            content: toSignalRContent(response.data),
            headers: response.headers,
          } as signalR.HttpResponse;
        },
        getCookieString: () => '',
      } as unknown as signalR.HttpClient,
      transport: signalR.HttpTransportType.LongPolling,
      accessTokenFactory,
    })
    .configureLogging(signalR.LogLevel.Information)
    .build();
};
